import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Text } from '@rsos/base-components';
import { TRANSCRIPT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import useHasAccessToSummarizationAndKeywords from '../../../../../hooks/useHasAccessToSummarizationAndKeywords';
import useHasAccessToTranscriptAndAudioPlayback from '../../../../../hooks/useHasAccessToTranscriptAndAudioPlayback';
import useTrackMissingTranscript from '../../../../../hooks/useTrackMissingTranscript';
import AudioPlayer from '../../AudioPlayer';
import CollaboratorBanner from '../CollaboratorBanner';
import IncidentSummary from './IncidentSummary';
import { TranscriptFeedback } from './TranscriptFeedback';
import TranscriptViewer from './TranscriptViewer';
import { TranscriptContent } from './TranscriptionTab.styles';
import useMissingTranscriptDelay from './hooks/useMissingTranscriptDelay';
import { selectTranscriptionForCall } from './transcriptionSlice';
import type { RootState } from './transcriptionSlice/transcriptionRootState';

type AppState = RootState & {
  eventPane: { selectedTab: string };
};

interface TranscriptionTabProps {
  callerId: string;
}

/**
 * The Transcript tab of the EventPane
 */
const TranscriptionTab = ({ callerId }: TranscriptionTabProps) => {
  const hasAccessToTranscriptionAndAudioPlayback =
    useHasAccessToTranscriptAndAudioPlayback();
  const hasAccessToSummarizationAndKeywords =
    useHasAccessToSummarizationAndKeywords();
  const hasData =
    hasAccessToTranscriptionAndAudioPlayback ||
    hasAccessToSummarizationAndKeywords;

  // Track if transcript tab is active
  const selectedTab = useSelector(
    (state: AppState) => state.eventPane.selectedTab,
  );
  const isTabActive = selectedTab === TRANSCRIPT_TAB;

  // Get call details for tracking
  const callDetails = useSelector(
    (state: AppState) => state.emergencyCalls.callQueue?.[callerId],
  );

  const callStatuses = callDetails?.callStatuses || [];
  const callStatus = callStatuses[callStatuses.length - 1] || null;
  const location0 = callDetails?.core?.locations?.[0] as unknown;
  const callStartTime = (() => {
    if (!location0 || typeof location0 !== 'object') return null;
    const loc = location0 as Record<string, unknown>;
    const conversationStart = loc.conversation_start_time;
    if (typeof conversationStart === 'number') return conversationStart;
    const callStart = loc.call_start_time;
    if (typeof callStart === 'number') return callStart;
    return null;
  })();

  // Track missing transcripts
  useTrackMissingTranscript(callerId, isTabActive, callStatus, callStartTime);

  const isTranscriptMaximized = useSelector(
    (state: AppState) => !!state?.transcriptions?.maximized,
  );

  const shouldRenderSummary =
    !isTranscriptMaximized && hasAccessToSummarizationAndKeywords;

  const currentTranscription = useSelector((state: AppState) =>
    selectTranscriptionForCall(callerId)(state),
  );
  const isFetching = !!currentTranscription?.fetching;
  const tokenCount = currentTranscription?.payload?.tokens?.length || 0;

  const hasWaitedForTranscript = useMissingTranscriptDelay({
    callerId,
    callStartTime,
    isFetching,
    tokenCount,
  });

  const showMissingCurrentTranscriptMessage = useMemo(() => {
    if (isFetching) return false;
    return tokenCount === 0 && hasWaitedForTranscript;
  }, [isFetching, tokenCount, hasWaitedForTranscript]);

  return (
    <Box gridRowGap="16px" height="100%">
      <CollaboratorBanner />
      <TranscriptFeedback callerId={callerId} />
      {hasAccessToTranscriptionAndAudioPlayback && (
        <AudioPlayer callerId={callerId} />
      )}
      {shouldRenderSummary && <IncidentSummary callerId={callerId} />}

      <TranscriptContent>
        {hasData ? (
          <>
            {hasAccessToTranscriptionAndAudioPlayback && (
              <>
                {showMissingCurrentTranscriptMessage ? (
                  <Box
                    border="1px solid"
                    borderColor="heavyLineOutline"
                    borderRadius="8px"
                    bg="background"
                    px="16px"
                    py="14px"
                    gridRowGap="10px"
                    align="center"
                    data-name="no-transcript-cta"
                  >
                    <Text
                      size="normal"
                      textAlign="center"
                      color="secondaryText"
                    >
                      No transcript is available for this call yet.
                    </Text>
                    <Text
                      size="normal"
                      textAlign="center"
                      color="secondaryText"
                    >
                      This can happen due to a hangup or an upstream error.
                    </Text>
                  </Box>
                ) : (
                  <TranscriptViewer
                    isTranscriptMaximized={isTranscriptMaximized}
                    callerId={callerId}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <Text textAlign="center" color="secondaryText">
            No Audio or Transcript available
          </Text>
        )}
      </TranscriptContent>
    </Box>
  );
};

export default TranscriptionTab;
